"use strict";
/**
 * Error Codes - Shared Types
 *
 * These error codes are used by both frontend and backend to handle
 * domain-specific errors programmatically.
 *
 * Design Pattern: Error Codes + Factory Methods (Modern Pattern)
 * - Frontend can react to specific error codes
 * - Backend throws domain errors with these codes
 * - Reduces boilerplate by 90% compared to one-class-per-error
 *
 * Note: Properly exported via barrel exports (src/types.ts and src/index.ts)
 *
 * See: resources/learning/20260107-error-handling-architecture-complete-analysis.md
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ErrorCodes = exports.HttpStatus = void 0;
/**
 * HTTP Status Codes for error responses
 */
var HttpStatus;
(function (HttpStatus) {
    HttpStatus[HttpStatus["BAD_REQUEST"] = 400] = "BAD_REQUEST";
    HttpStatus[HttpStatus["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
    HttpStatus[HttpStatus["FORBIDDEN"] = 403] = "FORBIDDEN";
    HttpStatus[HttpStatus["NOT_FOUND"] = 404] = "NOT_FOUND";
    HttpStatus[HttpStatus["CONFLICT"] = 409] = "CONFLICT";
    HttpStatus[HttpStatus["UNPROCESSABLE_ENTITY"] = 422] = "UNPROCESSABLE_ENTITY";
    HttpStatus[HttpStatus["INTERNAL_SERVER_ERROR"] = 500] = "INTERNAL_SERVER_ERROR";
})(HttpStatus || (exports.HttpStatus = HttpStatus = {}));
/**
 * Domain-specific error codes
 * Format: RESOURCE_ERROR_TYPE
 */
exports.ErrorCodes = {
    // ====================================
    // Collaborator Errors
    // ====================================
    COLLABORATOR_NOT_FOUND: {
        code: 'COLLABORATOR_NOT_FOUND',
        status: HttpStatus.NOT_FOUND,
        defaultMessage: 'Collaborator not found',
    },
    COLLABORATOR_ALREADY_REGISTERED: {
        code: 'COLLABORATOR_ALREADY_REGISTERED',
        status: HttpStatus.CONFLICT,
        defaultMessage: 'Collaborator is already registered',
    },
    COLLABORATOR_INACTIVE: {
        code: 'COLLABORATOR_INACTIVE',
        status: HttpStatus.FORBIDDEN,
        defaultMessage: 'Collaborator is inactive',
    },
    CANNOT_DELETE_ACTIVE_COLLABORATOR: {
        code: 'CANNOT_DELETE_ACTIVE_COLLABORATOR',
        status: HttpStatus.CONFLICT,
        defaultMessage: 'Cannot delete active collaborator',
    },
    DUPLICATE_COLLABORATOR_CODE: {
        code: 'DUPLICATE_COLLABORATOR_CODE',
        status: HttpStatus.CONFLICT,
        defaultMessage: 'Collaborator code already exists',
    },
    DUPLICATE_COLLABORATOR_EMAIL: {
        code: 'DUPLICATE_COLLABORATOR_EMAIL',
        status: HttpStatus.CONFLICT,
        defaultMessage: 'Email is already registered',
    },
    INVALID_ACCESS_CODE: {
        code: 'INVALID_ACCESS_CODE',
        status: HttpStatus.UNAUTHORIZED,
        defaultMessage: 'Invalid access code',
    },
    ACCESS_CODE_NOT_SET: {
        code: 'ACCESS_CODE_NOT_SET',
        status: HttpStatus.UNPROCESSABLE_ENTITY,
        defaultMessage: 'Access code not set for collaborator',
    },
    INVALID_FISCAL_DATA: {
        code: 'INVALID_FISCAL_DATA',
        status: HttpStatus.BAD_REQUEST,
        defaultMessage: 'Invalid fiscal data',
    },
    // ====================================
    // Authentication Errors
    // ====================================
    INVALID_CREDENTIALS: {
        code: 'INVALID_CREDENTIALS',
        status: HttpStatus.UNAUTHORIZED,
        defaultMessage: 'Invalid credentials',
    },
    TOKEN_EXPIRED: {
        code: 'TOKEN_EXPIRED',
        status: HttpStatus.UNAUTHORIZED,
        defaultMessage: 'Token has expired',
    },
    TOKEN_INVALID: {
        code: 'TOKEN_INVALID',
        status: HttpStatus.UNAUTHORIZED,
        defaultMessage: 'Invalid token',
    },
    // ====================================
    // Validation Errors
    // ====================================
    VALIDATION_ERROR: {
        code: 'VALIDATION_ERROR',
        status: HttpStatus.BAD_REQUEST,
        defaultMessage: 'Validation error',
    },
    // ====================================
    // Generic Errors
    // ====================================
    INTERNAL_ERROR: {
        code: 'INTERNAL_ERROR',
        status: HttpStatus.INTERNAL_SERVER_ERROR,
        defaultMessage: 'Internal server error',
    },
    NOT_FOUND: {
        code: 'NOT_FOUND',
        status: HttpStatus.NOT_FOUND,
        defaultMessage: 'Resource not found',
    },
    UNAUTHORIZED: {
        code: 'UNAUTHORIZED',
        status: HttpStatus.UNAUTHORIZED,
        defaultMessage: 'Unauthorized',
    },
    FORBIDDEN: {
        code: 'FORBIDDEN',
        status: HttpStatus.FORBIDDEN,
        defaultMessage: 'Forbidden',
    },
};
